import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// Neumorphic restyle: the default (accent) button is the design's green
// gradient raised control; press feedback swaps the drop shadow for an
// inset one (`.neu-pressable` transitions box-shadow only — perf rule).
const buttonVariants = cva(
  'neu-pressable inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'neu-gradient text-white shadow-neu-flat hover:shadow-neu-raised active:shadow-neu-press-dark',
        destructive:
          'bg-destructive text-destructive-foreground shadow-neu-flat hover:bg-destructive/90 active:shadow-neu-press-dark',
        outline:
          'border border-border/60 bg-transparent shadow-neu-raised hover:shadow-neu-raised-hover hover:text-accent-foreground active:shadow-neu-inset-press',
        secondary:
          'bg-secondary text-secondary-foreground shadow-neu-flat hover:bg-secondary/80 active:shadow-neu-inset-press',
        ghost: 'hover:bg-accent hover:text-accent-foreground active:shadow-neu-inset-sm',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
